import { useState, useEffect, useCallback } from 'react';
import { User, UsersResponse, Tenant } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface UseUsersParams {
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useUsers({ search, page = 1, pageSize = 20 }: UseUsersParams = {}) {
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allowedRoles, setAllowedRoles] = useState<string[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const { isAuthenticated, user } = useAuth();

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, []);

  const fetchTenants = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/users/tenants');
      if (response.ok) {
        const data = await response.json();
        setTenants(data.data.tenants || []);
      }
    } catch (error) {
      console.error('Erro ao buscar tenants:', error);
    }
  }, [authenticatedFetch]);

  const loadUsers = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (search) {
        params.set('search', search);
      }

      const response = await authenticatedFetch(`/api/users?${params}`);

      if (!response.ok) {
        throw new Error('Erro ao carregar usuários');
      }

      const responseData = await response.json();
      const data: UsersResponse = responseData.data || responseData;
      setUsers(data.users);
      setTotal(data.pagination.total);
      setTotalPages(data.pagination.totalPages);
      if (data.allowedRoles) {
        setAllowedRoles(data.allowedRoles);
      }

      // Se for SUPERADMIN, busca tenants disponíveis para o formulário
      if (user?.role === 'SUPERADMIN') {
        fetchTenants();
      }
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, pageSize, search, authenticatedFetch, user?.role, fetchTenants]);

  const deleteUser = useCallback(async (id: string) => {
    try {
      let url = `/api/users/${id}`;
      // Check if id is an invite ID (format: invite-UUID)
      if (id.startsWith('invite-')) {
        const inviteId = id.replace('invite-', '');
        url = `/api/invites/${inviteId}`;
      }

      const response = await authenticatedFetch(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar usuário');
      }

      await loadUsers();
      return true;
    } catch (err) {
      console.error('Erro ao deletar usuário:', err);
      throw err;
    }
  }, [authenticatedFetch, loadUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return {
    users,
    total,
    totalPages,
    loading,
    error,
    refresh: loadUsers,
    deleteUser,
    allowedRoles,
    tenants,
  };
}